import { z } from 'zod';
import type { TFunction } from 'i18next';

export interface DepositValidationConfig {
  operationType: string;
  translationKeys: {
    requiredField: string;
    minAmount: string;
  };
}

export const createDepositFormSchema = (
  t: TFunction,
  config: DepositValidationConfig,
  minAmount?: number,
  currencyCode?: string,
  maxAmount?: number
) => {
  const { translationKeys } = config;
  const required = t(translationKeys.requiredField);
  const minAmountMessage = minAmount
    ? t(translationKeys.minAmount, {
        amount: minAmount,
        currency: currencyCode || '',
      })
    : required;

  return z.object({
    currencyCode: z.string().nonempty({ message: required }),
    amount: z
      .string()
      .refine(val => (minAmount ? Number(val) >= minAmount : true), {
        message: minAmountMessage,
      })
      .refine(val => (maxAmount ? Number(val) <= maxAmount : true), {
        message: t('common.maxAmount', {
          amount: maxAmount,
          currency: currencyCode || '',
        }),
      }),
  });
};

export const DEPOSIT_VALIDATION_CONFIGS: Record<
  string,
  Omit<DepositValidationConfig, 'operationType'>
> = {
  sccrs: {
    translationKeys: {
      requiredField: 'depositMethods.sccrs.validation.requiredField',
      minAmount: 'depositMethods.sccrs.validation.minAmount',
    },
  },
  sbp: {
    translationKeys: {
      requiredField: 'depositMethods.sbp.validation.requiredField',
      minAmount: 'depositMethods.sbp.validation.minAmount',
    },
  },
  sberpay: {
    translationKeys: {
      requiredField: 'depositMethods.sberpay.validation.requiredField',
      minAmount: 'depositMethods.sberpay.validation.minAmount',
    },
  },
  card: {
    translationKeys: {
      requiredField: 'depositMethods.creditCard.validation.requiredField',
      minAmount: 'depositMethods.creditCard.validation.minAmount',
    },
  },
  bankTransfer: {
    translationKeys: {
      requiredField: 'depositMethods.bankTransfer.validation.requiredField',
      minAmount: 'depositMethods.bankTransfer.validation.minAmount',
    },
  },
  bank: {
    translationKeys: {
      requiredField: 'depositMethods.bankTransfer.validation.requiredField',
      minAmount: 'depositMethods.bankTransfer.validation.minAmount',
    },
  },
};

export const getDepositValidationConfig = (
  operationType: string
): DepositValidationConfig => {
  const config = DEPOSIT_VALIDATION_CONFIGS[operationType];
  if (!config) {
    throw new Error(
      `No validation config found for operation type: ${operationType}`
    );
  }

  return {
    operationType,
    ...config,
  };
};
